import React from 'react'
import { useAppSelector } from '../redux/hooks'
import {
  Users,
  Activity,
  Server,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Database,
} from 'lucide-react'

const Dashboard: React.FC = () => {
  const { metrics } = useAppSelector((state) => state.monitoring)
  const { clients } = useAppSelector((state) => state.clients)
  const { isConnected } = useAppSelector((state) => state.socket)
  
  const stats = [
    {
      label: 'Active Clients',
      value: clients.length.toString(),
      change: '+12%',
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      label: 'API Requests',
      value: metrics.requestsPerMinute.toString(),
      change: '+5%',
      icon: Activity,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      label: 'Server Load',
      value: `${metrics.cpu.toFixed(1)}%`,
      change: '-2%',
      icon: Server,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
    {
      label: 'Response Time',
      value: `${metrics.averageResponseTime}ms`,
      change: '-15%',
      icon: Clock,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
    },
  ]
  
  const systemStatus = [
    {
      service: 'API Gateway',
      status: 'operational',
      uptime: '99.99%',
    },
    {
      service: 'Database Cluster',
      status: 'operational',
      uptime: '99.95%',
    },
    {
      service: 'Redis Cache',
      status: 'operational',
      uptime: '100%',
    },
    {
      service: 'WebSocket Server',
      status: isConnected ? 'operational' : 'degraded',
      uptime: isConnected ? '99.9%' : '95%',
    },
  ]
  
  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">System Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Monitor and manage your SALEXT infrastructure</p>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2 rounded-md ${stat.bgColor}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <span className={`text-sm font-medium ${
                stat.change.startsWith('+') ? 'text-green-500' : 'text-red-500'
              }`}>
                {stat.change}
              </span>
            </div>
            <h3 className="text-xl font-semibold text-foreground">{stat.value}</h3>
            <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
          </div>
        ))}
      </div>
      
      {/* System Status and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* System Status */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-base font-medium text-foreground mb-3">System Status</h2>
          <div className="space-y-2">
            {systemStatus.map((service) => (
              <div key={service.service} className="flex items-center justify-between p-2 bg-muted/50 rounded-md">
                <div className="flex items-center space-x-2">
                  {service.status === 'operational' ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  )}
                  <span className="text-sm font-medium text-foreground">{service.service}</span>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-medium ${
                    service.status === 'operational' ? 'text-green-500' : 'text-yellow-500'
                  }`}>
                    {service.status}
                  </span>
                  <p className="text-xs text-muted-foreground">Uptime: {service.uptime}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-base font-medium text-foreground mb-3">Quick Actions</h2>
          <div className="space-y-2">
            <button className="w-full text-left p-3 bg-muted/50 hover:bg-muted rounded-md transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Database className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Database Backup</p>
                    <p className="text-xs text-muted-foreground">Last backup: 2 hours ago</p>
                  </div>
                </div>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </div>
            </button>
            
            <button className="w-full text-left p-3 bg-muted/50 hover:bg-muted rounded-md transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Server className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Clear Cache</p>
                    <p className="text-xs text-muted-foreground">Redis: 2.3GB used</p>
                  </div>
                </div>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </div>
            </button>
            
            <button className="w-full text-left p-3 bg-muted/50 hover:bg-muted rounded-md transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">View System Logs</p>
                    <p className="text-xs text-muted-foreground">3 warnings in last hour</p>
                  </div>
                </div>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </div>
            </button>
          </div>
        </div>
      </div>
      
      {/* Recent Activity Feed (placeholder) */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="text-base font-medium text-foreground mb-3">Recent Activity</h2>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">No recent activity to display</p>
        </div>
      </div>
    </div>
  )
}

export default Dashboard